import { Close, Download } from "@mui/icons-material";
import {
	AppBar,
	Box,
	Divider,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	Modal,
	Select,
	Skeleton,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { NumericFormat, PatternFormat } from "react-number-format";
import {
	createDocumentSocket,
	DocumentFormData,
	DocumentFormField,
	postDocumentSocket,
	SessionState,
	tempDocsUrl,
} from "../utils";

function blobToURL(blob: Blob) {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.readAsDataURL(blob);
		reader.onloadend = function () {
			const base64data = reader.result;
			resolve(base64data);
		};
	});
}

interface FormWindowContainerProps {
	formsData: {
		[id: string]: DocumentFormData;
	};
	selectedFormId: string | null;
	onClose: () => void;
	sessionState: SessionState;
}

export function FormWindowContainer({
	formsData,
	selectedFormId,
	onClose,
	sessionState,
}: FormWindowContainerProps) {
	const [webSocket, setWebSocket] = useState<WebSocket | null>(null);
	const [tempDocId, setTempDocId] = useState<string | null>(null);
	const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

	const webSocketHandler = async (action: string, payload: any) => {
		switch (action) {
			case "NEW FILE": {
				setTempDocId(payload.id);
				break;
			}
			case "FAILURE": {
				console.error(payload?.message);
				break;
			}
			case "UPDATE": {
				const arrayBuffer = await fetch(
					`${tempDocsUrl}${payload.id}.pdf`
				);
				const blob = await arrayBuffer.blob();
				setPdfBlobUrl((await blobToURL(blob)) as string);
				break;
			}
			case "FILE": {
				const a = document.createElement("a");
				document.body.appendChild(a);
				a.href = `${tempDocsUrl}${payload.id}.docx`;
				a.target = "_blank";
				a.click();
				break;
			}
		}
	};

	useEffect(() => {
		if (webSocket) {
			webSocket.close();
		}
		if (selectedFormId !== null) {
			const { filename } = formsData[selectedFormId];
			setWebSocket(
				createDocumentSocket(filename, webSocketHandler, sessionState)
			);
		}
	}, [selectedFormId]);

	return (
		<Modal open={selectedFormId !== null} onClose={() => onClose()}>
			{selectedFormId !== null &&
			tempDocId !== null &&
			webSocket !== null ? (
				<FormWindow
					sessionState={sessionState}
					webSocket={webSocket}
					tempDocId={tempDocId}
					formData={formsData[selectedFormId]}
					onClose={onClose}
					setPdfBlobUrlToNull={() => setPdfBlobUrl(null)}
					pdfBlobUrl={pdfBlobUrl}
				/>
			) : (
				<></>
			)}
		</Modal>
	);
}

interface FormWindowProps {
	formData: DocumentFormData;
	tempDocId: string;
	webSocket: WebSocket;
	sessionState: SessionState;
	pdfBlobUrl: string | null;
	setPdfBlobUrlToNull: () => void;
	onClose: () => void;
}

interface TextRunPatch {
	text: string;
	bold?: boolean;
	underline?: { type: string; color: string };
	color?: string;
	superScript?: boolean;
}

function getCompiledPatch(
	formData: DocumentFormData,
	currentPatches: {
		[index: string]: string;
	}
) {
	let patches: {
		[index: string]: {
			raw: string;
			runs: TextRunPatch[];
		};
	} = {};

	formData.fields.forEach((_, i) => {
		if (!currentPatches[`${i}`]) {
			patches[`${i}`] = {
				raw: "",
				runs: [{ text: `` }],
			};
		} else {
			patches[`${i}`] = {
				raw: currentPatches[`${i}`],
				runs: [
					{
						text: currentPatches[`${i}`].trim(),
					},
				],
			};
		}
	});
	return patches;
}

function getDefaultPatch(
	formData: DocumentFormData,
	currentPatches: {
		[index: string]: string;
	}
) {
	let patches: {
		[index: string]: {
			raw: string;
			runs: TextRunPatch[];
		};
	} = {};

	formData.fields.forEach((_, i) => {
		if (!currentPatches[`${i}`]) {
			patches[`${i}`] = {
				raw: "",
				runs: [
					{ text: `_______`, color: "0095ff", bold: true },
					{ text: ` (${i + 1})`, color: "0095ff", superScript: true },
				],
			};
		} else {
			patches[`${i}`] = {
				raw: currentPatches[`${i}`],
				runs: [
					{
						text: currentPatches[`${i}`].trim(),
						color: "0095ff",
						bold: true,
					},
					{ text: ` (${i + 1})`, color: "0095ff", superScript: true },
				],
			};
		}
	});
	return patches;
}

function FormWindow({
	formData,
	onClose,
	tempDocId,
	webSocket,
	sessionState,
	pdfBlobUrl,
	setPdfBlobUrlToNull,
}: FormWindowProps) {
	const { name } = formData;

	const [patches, setPatches] = useState<{ [index: string]: string }>({});

	const patchDocument = () => {
		setPdfBlobUrlToNull();
		postDocumentSocket(
			webSocket,
			"PATCHES",
			{ patches: getDefaultPatch(formData, patches), id: tempDocId },
			sessionState
		);
	};

	const downloadDocument = () => {
		postDocumentSocket(
			webSocket,
			"DOWNLOAD",
			{ patches: getCompiledPatch(formData, patches), id: tempDocId },
			sessionState
		);
	};

	useEffect(() => {
		try {
			patchDocument();
		} catch (e) {}
	}, [patches]);

	return (
		<Box
			sx={{
				position: "absolute",
				top: "15px",
				maxHeight: "calc(100% - 30px)",
				left: "30px",
				right: "30px",
				boxShadow: 24,
				background: (theme) => theme.palette.background.paper,
				overflow: "auto",
				borderRadius: "5px",
			}}
		>
			<AppBar position="sticky">
				<Toolbar>
					<Typography
						variant="h6"
						color="inherit"
						component="div"
						sx={{ flex: 1 }}
					>
						{name}
					</Typography>
					<Tooltip title="Download Document">
						<IconButton
							onClick={() => downloadDocument()}
							sx={{ color: "inherit" }}
						>
							<Download />
						</IconButton>
					</Tooltip>
					<Tooltip title="Close Window">
						<IconButton
							onClick={() => onClose()}
							sx={{ color: "inherit" }}
						>
							<Close />
						</IconButton>
					</Tooltip>
				</Toolbar>
			</AppBar>
			<Box
				sx={{
					position: "relative",
					width: "100%",
					display: "flex",
					minHeight: "700px",
				}}
			>
				<Box
					sx={{
						flexGrow: 1,
						minWidth: "300px",
						height: "700px",
						overflow: "auto",
					}}
				>
					{formData.fields.map((field, i) => (
						<FormWindowField
							index={i + 1}
							fieldData={field}
							onChange={(value) =>
								setPatches({
									...patches,
									[`${i}`]: value,
								})
							}
						/>
					))}
				</Box>
				<Box sx={{ width: 700, height: 700 }}>
					{pdfBlobUrl !== null ? (
						<iframe
							style={{ width: 700, height: 700 }}
							src={`${pdfBlobUrl}#toolbar=0&navbar=0`}
							frameBorder={0}
						></iframe>
					) : (
						<Skeleton
							variant="rectangular"
							width={700}
							height={700}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
}

interface FormWindowFieldProps {
	index: number;
	fieldData: DocumentFormField;
	onChange: (value: string) => void;
}

function FormWindowField({ index, fieldData, onChange }: FormWindowFieldProps) {
	const [isChanged, setIsChanged] = useState(false);

	const wrapper = (child: any) => (
		<Box
			sx={{
				margin: 2,
				borderRadius: "4px",
				display: "flex",
				flexDirection: "column",
				backgroundColor: (theme) => theme.palette.grey[900],
				border: (theme) => `0.5px solid ${theme.palette.grey[800]}`,
			}}
		>
			<Typography variant="body1" color="white" sx={{ m: 1 }}>
				{index}. {fieldData.label}
			</Typography>
			<Divider />
			{fieldData.desc && (
				<>
					<Typography variant="body2" color="white" sx={{ m: 1 }}>
						{fieldData.desc}
					</Typography>
					<Divider />
				</>
			)}
			<FormControl sx={{ m: 1, minWidth: 300 }}>{child}</FormControl>
		</Box>
	);

	switch (fieldData.type) {
		case "phone":
			return wrapper(
				<PatternFormat
					size="small"
					format="(###) ###-####"
					customInput={TextField}
					onChange={() => setIsChanged(true)}
					onBlur={(e) => {
						if (isChanged) {
							onChange(e.target.value);
							setIsChanged(false);
						}
					}}
				/>
			);
		case "text":
		case "email":
			return wrapper(
				<TextField
					size="small"
					onChange={() => setIsChanged(true)}
					onBlur={(e) => {
						if (isChanged) {
							onChange(e.target.value);
							setIsChanged(false);
						}
					}}
				/>
			);
		case "money":
			return wrapper(
				<NumericFormat
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">$</InputAdornment>
						),
					}}
					size="small"
					thousandSeparator=","
					customInput={TextField}
					onChange={() => setIsChanged(true)}
					onBlur={(e) => {
						if (isChanged) {
							onChange(e.target.value);
							setIsChanged(false);
						}
					}}
				/>
			);
		case "date":
			return wrapper(
				<DatePicker
					slotProps={{
						textField: {
							size: "small",
							onBlur: (e) => {
								if (isChanged) {
									onChange(
										dayjs(e.target.value).format(
											fieldData.format
										)
									);
									setIsChanged(false);
								}
							},
						},
					}}
					onAccept={(value) => {
						onChange(dayjs(value).format(fieldData.format));
						setIsChanged(false);
					}}
					onChange={() => setIsChanged(true)}
				/>
			);
		case "choice":
			return wrapper(
				<Select
					size="small"
					onChange={() => setIsChanged(true)}
					onBlur={(e) => {
						if (isChanged) {
							onChange(e.target.value);
							setIsChanged(false);
						}
					}}
				>
					{fieldData.choices?.map((choice) => (
						<MenuItem key={choice} value={choice}>
							{choice}
						</MenuItem>
					))}
				</Select>
			);
		case "text-long":
			return wrapper(
				<TextField
					multiline
					size="small"
					onChange={() => setIsChanged(true)}
					onBlur={(e) => {
						if (isChanged) {
							onChange(e.target.value);
							setIsChanged(false);
						}
					}}
				/>
			);
		default:
			return <></>;
	}
}
